import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  XIcon,
  CheckCircleIcon,
  UndoIcon,
  ExternalLinkIcon,
  Loader2Icon,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { fetchJobTimeEntries } from '../../api/jobRelatedRecords'
import { getStatusColor, formatStatusLabel } from '../../lib/jobStatusWorkflow'
import { JobTaskChecklist } from '../job/JobTaskChecklist'
import { ApartmentJobWork } from '../job/ApartmentJobWork'
import { JobGallery } from '../job/JobGallery'
import { ViewTabBar } from '../ViewPageLayout'
import type { ReviewQueueJob } from '../../api/reviewQueue'

interface ReviewJobQuickPanelProps {
  job: ReviewQueueJob
  canApprove: boolean
  onClose: () => void
  onUpdated: () => void
}

interface JobExtras {
  contract_id?: string | null
  service_type?: string | null
  notes?: string | null
}

export function ReviewJobQuickPanel({
  job,
  canApprove,
  onClose,
  onUpdated,
}: ReviewJobQuickPanelProps) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'checklist' | 'photos' | 'time' | 'notes'>('checklist')
  const [timeEntries, setTimeEntries] = useState<Awaited<ReturnType<typeof fetchJobTimeEntries>>>([])
  const [noteText, setNoteText] = useState(job.completion_notes || '')
  const [extras, setExtras] = useState<JobExtras>({})
  const [loadingExtras, setLoadingExtras] = useState(true)
  const [saving, setSaving] = useState(false)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    setNoteText(job.completion_notes || '')
  }, [job.completion_notes, job.id])

  useEffect(() => {
    let cancelled = false
    setLoadingExtras(true)
    Promise.all([
      supabase
        .from('jobs')
        .select('contract_id, service_type, notes')
        .eq('id', job.id)
        .single(),
      fetchJobTimeEntries(job.id),
    ])
      .then(([jobRes, entries]) => {
        if (cancelled) return
        const row = jobRes.data as any
        setExtras({
          contract_id: row?.contract_id ?? null,
          service_type: row?.service_type ?? job.service_type ?? null,
          notes: row?.notes ?? null,
        })
        setTimeEntries(entries)
      })
      .catch(() => {
        if (!cancelled) {
          setExtras({ service_type: job.service_type ?? null })
          setTimeEntries([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingExtras(false)
      })
    return () => {
      cancelled = true
    }
  }, [job.id, job.service_type])

  const saveNote = async () => {
    setSaving(true)
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ completion_notes: noteText.trim() || null })
        .eq('id', job.id)
      if (error) throw error
      onUpdated()
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (!canApprove) return
    const publish = window.confirm(
      "Publish this job's report and photos to the customer portal?"
    )
    setUpdating(true)
    try {
      const { error } = await supabase
        .from('jobs')
        .update({
          status: 'approved',
          portal_published_at: publish ? new Date().toISOString() : null,
        })
        .eq('id', job.id)
      if (error) throw error
      onUpdated()
      onClose()
    } catch (err: any) {
      alert('Failed to approve job: ' + (err.message || 'Unknown error'))
    } finally {
      setUpdating(false)
    }
  }

  const handleSendBack = async () => {
    if (!canApprove) return
    if (!window.confirm('Send this job back to the field for rework?')) return
    setUpdating(true)
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: 'in_progress' })
        .eq('id', job.id)
      if (error) throw error
      onUpdated()
      onClose()
    } catch (err: any) {
      alert('Failed to update job: ' + (err.message || 'Unknown error'))
    } finally {
      setUpdating(false)
    }
  }

  const canApproveJob =
    canApprove &&
    job.status !== 'approved' &&
    ['pending_review', 'under_review', 'completed', 'qc_failed'].includes(job.status)
  const canSendBack =
    canApprove &&
    job.status !== 'in_progress' &&
    job.status !== 'scheduled' &&
    job.status !== 'draft' &&
    job.status !== 'approved'

  const propertyLine = [job.property?.address, job.property?.city].filter(Boolean).join(', ')

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-lg bg-white shadow-xl h-full flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">{job.job_number ? `#${job.job_number}` : 'Job'}</p>
            <h2 className="text-lg font-semibold text-gray-900 truncate">{job.title}</h2>
            <span
              className={`inline-flex mt-1 px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(job.status)}`}
            >
              {formatStatusLabel(job.status)}
            </span>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-100 text-sm text-gray-600 space-y-1">
          <div>{job.customer?.company_name || '—'}</div>
          <div>{propertyLine || '—'}</div>
          {job.techs.length > 0 && <div>Crew: {job.techs.join(', ')}</div>}
          {job.actual_end_time && (
            <div>Completed: {new Date(job.actual_end_time).toLocaleString()}</div>
          )}
          <div>Photos: {job.photos}</div>
        </div>

        <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate(`/review/${job.id}`)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            <ExternalLinkIcon className="w-4 h-4" />
            Full review
          </button>
        </div>

        {(canApproveJob || canSendBack) && (
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-2">
            {canApproveJob && (
              <button
                type="button"
                disabled={updating}
                onClick={handleApprove}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircleIcon className="w-4 h-4" />
                {updating ? 'Updating…' : 'Approve'}
              </button>
            )}
            {canSendBack && (
              <button
                type="button"
                disabled={updating}
                onClick={handleSendBack}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                <UndoIcon className="w-4 h-4" />
                Needs revision
              </button>
            )}
          </div>
        )}

        <ViewTabBar
          tabs={[
            { id: 'checklist', label: 'Checklist' },
            { id: 'photos', label: 'Photos' },
            { id: 'time', label: 'Time' },
            { id: 'notes', label: 'Notes' },
          ]}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as typeof activeTab)}
          className="px-4"
        />

        <div className="flex-1 overflow-y-auto p-4">
          {loadingExtras && activeTab === 'checklist' ? (
            <div className="flex justify-center py-8">
              <Loader2Icon className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {activeTab === 'checklist' && (
                <div className="space-y-4">
                  <ApartmentJobWork jobId={job.id} serviceType={extras.service_type} />
                  <JobTaskChecklist
                    jobId={job.id}
                    contractId={extras.contract_id}
                    serviceType={extras.service_type}
                    generalOnly
                  />
                </div>
              )}
              {activeTab === 'photos' && <JobGallery jobId={job.id} />}
              {activeTab === 'time' && (
                <div className="space-y-2 text-sm">
                  {timeEntries.length === 0 ? (
                    <p className="text-gray-500">No time entries.</p>
                  ) : (
                    timeEntries.map((e) => (
                      <div key={e.id} className="border border-gray-100 rounded-lg p-2">
                        <div className="font-medium">
                          {e.employee?.first_name} {e.employee?.last_name}
                        </div>
                        <div className="text-gray-600">
                          {new Date(e.clock_in_time).toLocaleString()}
                          {e.clock_out_time
                            ? ` – ${new Date(e.clock_out_time).toLocaleString()}`
                            : ' (open)'}
                        </div>
                        {e.total_hours != null && <div>{Number(e.total_hours).toFixed(2)} hrs</div>}
                      </div>
                    ))
                  )}
                </div>
              )}
              {activeTab === 'notes' && (
                <div className="space-y-3">
                  {extras.notes && (
                    <div className="text-sm text-gray-600 border border-gray-100 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Job notes</p>
                      <p>{extras.notes}</p>
                    </div>
                  )}
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    rows={5}
                    disabled={!canApprove}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Office / completion notes"
                  />
                  {canApprove && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={saveNote}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      Save note
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
